import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Candidate|History|Credential Requests|Manual Change Credential Modal|';
const sharedProperties: Pick<LocDef, 'shared' | 'feature' | 'portal'> = {
	shared: false,
	feature: HubFeature.Credential,
	portal: 'SP',
};

type LocNameStart = 'SPCandidateHistoryManualChangeCredential';
export type SPCandidateHistoryManualChangeCredentialLocNames =
	| `${LocNameStart}TitleModal`
	| `${LocNameStart}ContentInstructions`
	| `${LocNameStart}ButtonViewCommonScenarios`
	| `${LocNameStart}ButtonHideCommonScenarios`
	| `${LocNameStart}ContentCommonScenarios`
	| `${LocNameStart}LabelCredential`
	| `${LocNameStart}ErrorCredentialRequired`
	| `${LocNameStart}ErrorCredentialNoMatch`
	| `${LocNameStart}LabelEffectiveDate`
	| `${LocNameStart}ErrorEffectiveDateRequired`
	| `${LocNameStart}ErrorEffectiveDateMin`
	| `${LocNameStart}LabelInactiveDate`
	| `${LocNameStart}ErrorInactiveDateRequired`
	| `${LocNameStart}ErrorInactiveDateMax`
	| `${LocNameStart}LabelExpireDate`
	| `${LocNameStart}ErrorExpireDateMax`
	| `${LocNameStart}ErrorInvalidDateRanges`
	| `${LocNameStart}LabelIgnoreLinkedExtensions`
	| `${LocNameStart}ContentLinkedExtensionInstructions`
	| `${LocNameStart}LabelComments`
	| `${LocNameStart}ErrorCommentsRequired`
	| `${LocNameStart}LabelStatus`
	| `${LocNameStart}LabelStatusNoChange`
	| `${LocNameStart}LabelStatusRemoveEmeritus`
	| `${LocNameStart}ErrorStatusRequired`
	| `${LocNameStart}ErrorStatusNoMatch`
	| `${LocNameStart}ButtonCancel`
	| `${LocNameStart}ButtonContinue`;

export const spCandidateHistoryManualChangeCredentialLocs: LocDefs<SPCandidateHistoryManualChangeCredentialLocNames> =
	{
		SPCandidateHistoryManualChangeCredentialTitleModal: {
			defaultValue: 'Manual Change',
			description: 'Modal Title',
			displayPath: `${pathStart}Modal Title`,
			displayType: 'Title',
			legacyName: 'Credential/ChangeCredentialModal.Title.ManualChange',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialContentInstructions: {
			defaultValue: `
            <p>Choose the credential instance to modify.</p>
		<br />
		<p>
			Enter the date on which your change will become effective and the new inactive date. The
			inactive date is the date on which the cert should expire or enter suspended status (if
			suspended status is used).
			<strong>The inactive date field should always be supplied.</strong>
		</p>
		<br />
		<p>
			If suspended periods apply to your program, the expiration date may need to be entered.
			A credential is considered suspended when it is no longer active (after the inactive
			date), but it has not yet expired (before the expiration date).
			<strong
				>If you do not supply an expire date, the expire date will be automatically
				calculated based on suspend period rules for that credential type.</strong
			>
			If you want to make a manual adjustment with a non-standard suspend period, enter the
			desired expire date as well as the inactive (suspend) date.
		</p>
		<br />
		<p>
			The system will reject any request that would result in the instance overlapping with
			another for the same credential; you cannot use an effective date that is before the
			date that the instance was granted or after it expired. You cannot use an effective date
			in the future.
		</p>`,
			description: 'Instructions content',
			displayPath: `${pathStart}Instructions`,
			displayType: 'Content',
			legacyName: 'Credential/ChangeCredentialModal.Content.Instructions',
			htmlAllowed: true,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialButtonViewCommonScenarios: {
			defaultValue: 'View Common Scenarios',
			description: 'Button to view common scenarios',
			displayPath: `${pathStart}View Common Scenarios`,
			displayType: 'Button',
			legacyName: 'Credential/ChangeCredentialModal.Button.ViewCommonScenarios',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialButtonHideCommonScenarios: {
			defaultValue: 'Hide Common Scenarios',
			description: 'Button to hide common scenarios',
			displayPath: `${pathStart}Hide Common Scenarios`,
			displayType: 'Button',
			legacyName: 'Credential/ChangeCredentialModal.Button.HideCommonScenarios',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialContentCommonScenarios: {
			defaultValue: `
            <ul class="styled">
					<li class="styled">
						For certifications without a suspended period configured
						<ul class="styled">
							<li class="styled">
								Extend the time where a certification instance is active:
								<ol class="styled">
									<li class="styled">
										If the credential has not expired yet, use today as the
										effective date. If the credential has already expired, use
										the current expiration date as the effective date.
									</li>
									<li class="styled">Specify the new inactive date.</li>
									<li class="styled">Leave the expire date blank.</li>
								</ol>
							</li>
						</ul>
					</li>
					<li class="styled">
						For certifications with a suspended period configured
						<ul class="styled">
							<li class="styled">
								Extend the time where a certification instance is active:
								<ol class="styled">
									<li class="styled">
										If the credential has not expired yet, use today as the
										effective date. If it is currently suspended or expired, use
										the current inactive date as the effective date.
									</li>
									<li class="styled">Specify the new inactive date.</li>
									<li class="styled">
										Leave the expire date blank (the system will auto-calculate
										it for you).
									</li>
								</ol>
							</li>
							<li class="styled">
								Extend the time where the certification instance is suspended
								(without changing when the instance became/becomes suspended):
								<ol class="styled">
									<li class="styled">
										If the credential has not expired yet, use today as the
										effective date. If it is currently suspended or expired, use
										the current inactive date as the effective date.
									</li>
									<li class="styled">Specify the new expire date.</li>
								</ol>
							</li>
						</ul>
					</li>
				</ul>`,
			description: 'Common scenarios content',
			displayPath: `${pathStart}Common Scenarios`,
			displayType: 'Content',
			legacyName: 'Credential/ChangeCredentialModal.Content.CommonScenarios',
			htmlAllowed: true,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialLabelCredential: {
			defaultValue: 'Credential',
			description: 'Label for credential field',
			displayPath: `${pathStart}Credential`,
			displayType: 'Label',
			legacyName: 'Credential/ChangeCredentialModal.FieldLabel.Credential',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialErrorCredentialRequired: {
			defaultValue: 'This field is required',
			description: 'Error message for required credential field',
			displayPath: `${pathStart}Credential Required`,
			displayType: 'Error',
			legacyName: 'Credential/ChangeCredentialModal.FieldError.CredentialRequired',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialErrorCredentialNoMatch: {
			defaultValue: 'You must choose an option from the list',
			description: 'Error message for no match in credential field',
			displayPath: `${pathStart}Credential No Match`,
			displayType: 'Error',
			legacyName: 'Credential/ChangeCredentialModal.FieldError.CredentialNoMatch',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialLabelEffectiveDate: {
			defaultValue: 'Effective Date',
			description: 'Label for effective date field',
			displayPath: `${pathStart}Effective Date`,
			displayType: 'Label',
			legacyName: 'Credential/ChangeCredentialModal.FieldLabel.EffectiveDate',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialErrorEffectiveDateRequired: {
			defaultValue: 'This field is required',
			description: 'Error message for required effective date field',
			displayPath: `${pathStart}Effective Date Required`,
			displayType: 'Error',
			legacyName: 'Credential/ChangeCredentialModal.FieldError.EffectiveDateRequired',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialErrorEffectiveDateMin: {
			defaultValue: 'Effective date must be later than the catalog active date',
			description: 'Error message for minimum effective date',
			displayPath: `${pathStart}Effective Date Min`,
			displayType: 'Error',
			legacyName: 'Credential/ChangeCredentialModal.FieldError.EffectiveDateMin',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialLabelInactiveDate: {
			defaultValue: 'Inactive Date',
			description: 'Label for inactive date field',
			displayPath: `${pathStart}Inactive Date`,
			displayType: 'Label',
			legacyName: 'Credential/ChangeCredentialModal.FieldLabel.InactiveDate',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialErrorInactiveDateRequired: {
			defaultValue: 'Inactive date is required',
			description: 'Error message for required inactive date field',
			displayPath: `${pathStart}Inactive Date Required`,
			displayType: 'Error',
			legacyName: 'Credential/ChangeCredentialModal.FieldError.InactiveDateRequired',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialErrorInactiveDateMax: {
			defaultValue: 'Inactive date must be before than the catalog expire date',
			description: 'Error message for maximum inactive date',
			displayPath: `${pathStart}Inactive Date Max`,
			displayType: 'Error',
			legacyName: 'Credential/GrantCredentialModal.FieldError.InactiveDateMax',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialLabelExpireDate: {
			defaultValue: 'Expire Date',
			description: 'Label for expire date field',
			displayPath: `${pathStart}Expire Date`,
			displayType: 'Label',
			legacyName: 'Credential/ChangeCredentialModal.FieldLabel.ExpireDate',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialErrorExpireDateMax: {
			defaultValue: 'Expire date must be before than the catalog expire date',
			description: 'Error message for maximum expire date',
			displayPath: `${pathStart}Expire Date Max`,
			displayType: 'Error',
			legacyName: 'Credential/GrantCredentialModal.FieldError.ExpireDateMax',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialErrorInvalidDateRanges: {
			defaultValue:
				'Invalid date ranges. Effective date must be the earliest. Expire date must be the latest, if set.',
			description: 'Error message for invalid date ranges',
			displayPath: `${pathStart}Invalid Date Ranges`,
			displayType: 'Error',
			legacyName: 'Credential/ChangeCredentialModal.FieldError.InvalidDateRanges',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialLabelIgnoreLinkedExtensions: {
			defaultValue: 'Ignore Linked Extensions',
			description: 'Label for ignore linked extensions checkbox',
			displayPath: `${pathStart}Ignore Linked Extensions`,
			displayType: 'Label',
			legacyName: 'Credential/ChangeCredentialModal.FieldLabel.IgnoreLinkedExtensions',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialContentLinkedExtensionInstructions: {
			defaultValue:
				'In most cases this should not be checked. A change will trigger the linked extension rules to appropriately extend any linked credentials. Check the box if you do not wish for this to happen.',
			description: 'Instructions for linked extension',
			displayPath: `${pathStart}Linked Extension Instructions`,
			displayType: 'Content',
			legacyName: 'Credential/ChangeCredentialModal.Content.LinkedExtensionInstructions',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialLabelComments: {
			defaultValue: 'Comments',
			description: 'Label for comments field',
			displayPath: `${pathStart}Comments`,
			displayType: 'Label',
			legacyName: 'Credential/ChangeCredentialModal.FieldLabel.Comments',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialErrorCommentsRequired: {
			defaultValue: 'This field is required',
			description: 'Error message for required comments field',
			displayPath: `${pathStart}Comments Required`,
			displayType: 'Error',
			legacyName: 'Credential/ChangeCredentialModal.FieldError.CommentsRequired',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialLabelStatus: {
			defaultValue: 'Status',
			description: 'Label for status field',
			displayPath: `${pathStart}Status`,
			displayType: 'Label',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialLabelStatusNoChange: {
			defaultValue: 'No Change',
			description: 'Label for the no change cert status option.',
			displayPath: `${pathStart}Status No Change`,
			displayType: 'Label',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialLabelStatusRemoveEmeritus: {
			defaultValue: 'Remove Emeritus',
			description: 'Label for the remove emeritus cert status option.',
			displayPath: `${pathStart}Status Remove Emeritus`,
			displayType: 'Label',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialErrorStatusRequired: {
			defaultValue: 'This field is required',
			description: 'Error message for required status field',
			displayPath: `${pathStart}Status Required`,
			displayType: 'Error',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialErrorStatusNoMatch: {
			defaultValue: 'You must choose an option from the list',
			description: 'Error message for no match in status field',
			displayPath: `${pathStart}Status No Match`,
			displayType: 'Error',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialButtonCancel: {
			defaultValue: 'Cancel',
			description: 'Cancel button text',
			displayPath: `${pathStart}Cancel`,
			displayType: 'Button',
			legacyName: 'Credential/ChangeCredential.Modal.Button.Cancel',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryManualChangeCredentialButtonContinue: {
			defaultValue: 'Continue',
			description: 'Continue button text',
			displayPath: `${pathStart}Continue`,
			displayType: 'Button',
			legacyName: 'Credential/ChangeCredential.Modal.Button.Continue',
			htmlAllowed: false,
			...sharedProperties,
		},
	};

export type SPCandidateHistoryManualChangeCredentialLocType =
	LocMap<SPCandidateHistoryManualChangeCredentialLocNames>;
